import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { ArrowLeft, PlayCircle, FileText, Link as LinkIcon, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../api';

interface Module {
  _id: string;
  title: string;
  type: 'video' | 'pdf' | 'text' | 'link' | 'document';
  content: string; // URL for video/pdf/link, actual text for text type
  duration?: number;
}

const CourseViewerPage: React.FC = () => {
  const { courseId, sectionId, moduleId } = useParams<{ courseId: string; sectionId: string; moduleId: string }>();
  const navigate = useNavigate();

  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModule = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/professor/modules/${moduleId}`);
        if (response.data && response.data.module) {
          setModule(response.data.module);
        } else {
          toast.error('Module non trouvé');
          navigate(`/professor/courses/${courseId}`);
        }
      } catch (error) {
        console.error('Error fetching module:', error);
        toast.error('Erreur lors du chargement du module');
        navigate(`/professor/courses/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    fetchModule();
  }, [moduleId, courseId, navigate]);

  const renderModuleContent = () => {
    if (!module) return null;

    switch (module.type) {
      case 'video':
        // Handle YouTube, Vimeo, or direct video links
        if (module.content && (module.content.includes('youtube.com') || module.content.includes('youtu.be'))) {
          const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/)([^&?]*)/;
          const match = module.content.match(youtubeRegex);
          const videoId = match && match[1] ? match[1] : null;
          return (
            <div className="aspect-video w-full">
              <iframe
                className="w-full h-full rounded-lg shadow-md"
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={module.title}
              ></iframe>
            </div>
          );
        } else if (module.content && module.content.includes('vimeo.com')) {
          const videoId = module.content.split('/').pop();
          return (
            <div className="aspect-video w-full">
              <iframe
                className="w-full h-full rounded-lg shadow-md"
                src={`https://player.vimeo.com/video/${videoId}`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={module.title}
              ></iframe>
            </div>
          );
        } else {
          // Direct video file (e.g., .mp4)
          return (
            <video controls className="w-full h-auto rounded-lg shadow-md">
              <source src={module.content} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          );
        }
      case 'pdf':
        return (
          <iframe
            src={module.content}
            className="w-full h-screen rounded-lg shadow-md"
            title={module.title}
          ></iframe>
        );
      case 'text':
        return (
          <div className="prose max-w-none bg-gray-50 p-6 rounded-lg shadow-sm">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{module.title}</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{module.content}</p>
          </div>
        );
      case 'link':
        return (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm text-center">
            <LinkIcon className="h-12 w-12 text-brand-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Accéder au lien externe</h2>
            <a
              href={module.content}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline font-medium text-lg"
            >
              {module.content}
            </a>
            <p className="text-sm text-gray-500 mt-2">Le lien s'ouvrira dans un nouvel onglet.</p>
          </div>
        );
      case 'document': // For .doc, .docx, etc. - might need a viewer service or direct download
        return (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm text-center">
            <FileText className="h-12 w-12 text-brand-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Télécharger le document</h2>
            <a
              href={module.content}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline font-medium text-lg"
              download
            >
              {module.title || 'Document'} (Cliquez pour télécharger)
            </a>
            <p className="text-sm text-gray-500 mt-2">Le document sera téléchargé.</p>
          </div>
        );
      default:
        return (
          <div className="bg-brand-100 border border-brand-400 text-brand-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Erreur:</strong>
            <span className="block sm:inline"> Type de module non pris en charge.</span>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  if (!module) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Module non trouvé</h3>
          <button
            onClick={() => navigate(`/professor/courses/${courseId}`)}
            className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            Retour au cours
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => navigate(`/professor/courses/${courseId}`)}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">{module.title}</h1>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {renderModuleContent()}
        </div>
      </div>
    </Layout>
  );
};

export default CourseViewerPage;